// Rating -> Arabic thank-you copy. The tone of what a reviewer reads after submitting is the
// whole point of this module: a 1-star reviewer who is congratulated reads it as the platform
// siding with the merchant, and a 5-star reviewer who is thanked for "honesty" reads it as
// suspicion. One mapping, resolved here and in `ReviewThankYou.tsx` only, never in a caller.
//
// ZERO IMPORTS ON PURPOSE. `frontend/vitest.config.ts` runs with `environment: 'node'` and no `@`
// alias, so any import here would break collection of the suite that guards this copy.

const reviewThankYouTones = {
  warm: 'warm',
  neutral: 'neutral',
  honest: 'honest',
} as const;

export type ReviewThankYouTone = keyof typeof reviewThankYouTones;

export type ReviewThankYouCopy = {
  tone: ReviewThankYouTone;
  title: string;
  body: string;
  reminder: string;
  searchCta: string;
  storeCta: string;
};

// Shared across all three tones: the CTAs are navigation labels, not sentiment. Keeping them
// identical is what lets the cross-sell button read the same everywhere the state renders.
const SEARCH_CTA = 'دوّر على متجر تاني';
const STORE_CTA = 'عرض صفحة المتجر';
// Content neutrality (CLAUDE.md): the reminder points at the habit, never at a specific store.
const REMINDER = 'خلي بالك، قبل أي عملية شراء جاية اتفرج على تقييمات المتجر الأول.';

const COPY_BY_TONE: Record<ReviewThankYouTone, ReviewThankYouCopy> = {
  warm: {
    tone: reviewThankYouTones.warm,
    title: 'شكراً لتقييمك',
    body: 'تجربتك الحلوة اتسجلت، وهتساعد مشترين تانيين يوصلوا للمتجر ده بثقة.',
    reminder: REMINDER,
    searchCta: SEARCH_CTA,
    storeCta: STORE_CTA,
  },
  neutral: {
    tone: reviewThankYouTones.neutral,
    title: 'شكراً على رأيك',
    body: 'التقييمات المتوازنة زي بتاعتك هي أكتر حاجة بتفيد المشترين وقت القرار.',
    reminder: REMINDER,
    searchCta: SEARCH_CTA,
    storeCta: STORE_CTA,
  },
  honest: {
    tone: reviewThankYouTones.honest,
    title: 'شكراً على صراحتك',
    body: 'صراحتك بتحمي مشترين تانيين من نفس التجربة، وده بالظبط اللي المنصة موجودة عشانه.',
    reminder: REMINDER,
    searchCta: SEARCH_CTA,
    storeCta: STORE_CTA,
  },
};

// Invalid input (0, 6, NaN, a half-star) resolves to `neutral` rather than throwing: this copy is
// rendered right after a successful submit, and a blank card there would read as a failed save.
export const reviewThankYouCopy = (rating: number): ReviewThankYouCopy => {
  const valid = Number.isInteger(rating) && rating >= 1 && rating <= 5;
  if (!valid) return COPY_BY_TONE.neutral;
  if (rating >= 4) return COPY_BY_TONE.warm;
  if (rating <= 2) return COPY_BY_TONE.honest;
  return COPY_BY_TONE.neutral;
};
